import { useLanguage } from '@/contexts/LanguageContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { motion } from 'framer-motion';

const faqData = {
  de: {
    title: 'Häufige Fragen',
    subtitle: 'Alles Wichtige rund um Taxi & Transfers in Salzburg',
    categories: [
      {
        name: 'Buchung & Ablauf',
        items: [
          { q: 'Wie kann ich ein Taxi oder eine Tour buchen?', a: 'Sie können Ihre Fahrt bequem per Telefon, E-Mail, WhatsApp, Buchungsformular oder direkt online auf unserer Website buchen. Für kurzfristige Fahrten empfehlen wir einen Anruf, damit wir sofort reagieren können.' },
          { q: 'Wie kurzfristig kann ich eine Fahrt buchen?', a: 'Spontane Transfers sind möglich, am besten rufen Sie uns direkt an. Online- oder Formularbuchungen eignen sich vor allem für geplante Fahrten.' },
          { q: 'Wo ist der Treffpunkt am Flughafen Salzburg?', a: 'Unsere Fahrer holen Sie direkt an Ihrem gewünschten Treffpunkt ab – egal ob in der Ankunftshalle, am Terminalausgang oder an einem individuellen Standort.' },
          { q: 'Kann ich meine Abholzeit ändern?', a: 'In den meisten Fällen können Abholzeiten flexibel angepasst werden. Bitte setzen Sie sich rechtzeitig mit uns in Verbindung, damit wir Änderungen problemlos berücksichtigen können.' },
          { q: 'Kann ich eine Fahrt stornieren?', a: 'Ja, eine kostenlose Stornierung ist bis 24 Stunden vor Fahrtbeginn möglich. Bei kurzfristigeren Absagen bitten wir um direkte Kontaktaufnahme, damit wir eine Lösung finden können.' },
        ],
      },
      {
        name: 'Preise & Service',
        items: [
          { q: 'Gibt es feste Preise für die Fahrten?', a: 'Ja, für die meisten Transfers bieten wir transparente Fixpreise. Sollte eine Strecke nicht vorab genau bestimmbar sein, kann die Abrechnung nach gefahrenen Kilometern oder Zeit erfolgen.' },
          { q: 'Sind Kindersitze im Taxi verfügbar?', a: 'In Österreich besteht keine Pflicht für Kindersitze im Taxi. Wenn Sie dennoch einen Sitz wünschen, stellen wir diesen selbstverständlich kostenlos zur Verfügung. Bitte geben Sie Ihren Wunsch bei der Buchung an.' },
          { q: 'Was passiert, wenn ich mich verspäte?', a: 'Eine Verspätung von bis zu 10 Minuten ist im Fahrpreis inkludiert. Danach können zusätzliche Wartezeiten berechnet werden.' },
          { q: 'Welche Zahlungsmöglichkeiten gibt es?', a: 'Sie können bequem bar oder mit Karte direkt beim Fahrer bezahlen.' },
          { q: 'Fallen Zuschläge für Nacht- oder Feiertagsfahrten an?', a: 'Nein, bei uns gibt es keine Zuschläge für Nachtfahrten oder Feiertage. Sie zahlen immer den vereinbarten Preis – ohne versteckte Kosten.' },
        ],
      },
      {
        name: 'Fahrzeuge & Ausstattung',
        items: [
          { q: 'Wie viele Personen können mitfahren?', a: 'Unsere größten Fahrzeuge bieten Platz für bis zu 7 Personen. Für größere Gruppen können problemlos mehrere Fahrzeuge gleichzeitig gebucht werden.' },
          { q: 'Gibt es Fahrzeuge für Gruppen oder viel Gepäck?', a: 'Ja, wir haben geräumige Vans und Fahrzeuge mit viel Stauraum – ideal für Familien, Skiausrüstung oder zusätzliches Gepäck.' },
          { q: 'Sind alle Fahrzeuge klimatisiert?', a: 'Ja, sämtliche Fahrzeuge verfügen über eine moderne Klimaanlage, damit Sie auch an heißen Tagen entspannt reisen.' },
          { q: 'Kann ich ein bestimmtes Fahrzeug auswählen?', a: 'Ja, auf Wunsch können Sie zwischen verschiedenen Fahrzeugtypen wie Limousine oder Van wählen.' },
          { q: 'Bieten Sie auch E-Fahrzeuge an?', a: 'Ja, wir setzen zunehmend auch auf Elektrofahrzeuge, um Ihre Fahrt noch nachhaltiger zu gestalten.' },
        ],
      },
    ],
  },
  en: {
    title: 'Frequently Asked Questions',
    subtitle: 'Everything you need to know about our taxi & transfer services',
    categories: [
      {
        name: 'Booking & Process',
        items: [
          { q: 'How can I book a taxi or tour?', a: 'You can book your ride conveniently by phone, email, WhatsApp, booking form, or directly online on our website. For short-notice rides, we recommend calling so we can respond immediately.' },
          { q: 'How short-notice can I book a ride?', a: 'Spontaneous transfers are possible — it\'s best to call us directly. Online or form bookings are ideal for planned trips.' },
          { q: 'Where is the meeting point at Salzburg Airport?', a: 'Our drivers pick you up at your desired meeting point — whether in the arrivals hall, at the terminal exit, or at a custom location.' },
          { q: 'Can I change my pickup time?', a: 'In most cases, pickup times can be adjusted flexibly. Please contact us in time so we can accommodate changes smoothly.' },
          { q: 'Can I cancel a ride?', a: 'Yes, free cancellation is possible up to 24 hours before departure. For shorter-notice cancellations, please contact us directly so we can find a solution.' },
        ],
      },
      {
        name: 'Prices & Service',
        items: [
          { q: 'Are there fixed prices for rides?', a: 'Yes, we offer transparent fixed prices for most transfers. If a route cannot be determined in advance, billing may be based on kilometers driven or time.' },
          { q: 'Are child seats available in the taxi?', a: 'In Austria, child seats are not mandatory in taxis. If you still want one, we provide it free of charge. Please mention your request when booking.' },
          { q: 'What happens if I\'m late?', a: 'A delay of up to 10 minutes is included in the fare. After that, additional waiting charges may apply.' },
          { q: 'What payment methods are available?', a: 'You can pay conveniently by cash or card directly with the driver.' },
          { q: 'Are there surcharges for night or holiday rides?', a: 'No, we have no surcharges for night or holiday rides. You always pay the agreed price — no hidden costs.' },
        ],
      },
      {
        name: 'Vehicles & Equipment',
        items: [
          { q: 'How many passengers can ride?', a: 'Our largest vehicles accommodate up to 7 passengers. For larger groups, multiple vehicles can be booked simultaneously.' },
          { q: 'Are there vehicles for groups or lots of luggage?', a: 'Yes, we have spacious vans and vehicles with ample storage — ideal for families, ski equipment, or extra luggage.' },
          { q: 'Are all vehicles air-conditioned?', a: 'Yes, all vehicles feature modern air conditioning for a relaxed journey even on hot days.' },
          { q: 'Can I choose a specific vehicle?', a: 'Yes, you can choose between different vehicle types such as sedan or van upon request.' },
          { q: 'Do you offer electric vehicles?', a: 'Yes, we increasingly use electric vehicles to make your ride more sustainable.' },
        ],
      },
    ],
  },
  tr: {
    title: 'Sıkça Sorulan Sorular',
    subtitle: 'Taksi ve transfer hizmetlerimiz hakkında merak edilenler',
    categories: [
      {
        name: 'Rezervasyon & Süreç',
        items: [
          { q: 'Nasıl taksi veya tur rezervasyonu yapabilirim?', a: 'Telefon, e-posta, WhatsApp, rezervasyon formu veya doğrudan web sitemiz üzerinden kolayca rezervasyon yapabilirsiniz. Kısa süreli yolculuklar için hemen yanıt verebilmemiz adına aramayı öneririz.' },
          { q: 'Ne kadar kısa sürede rezervasyon yapabilirim?', a: 'Spontan transferler mümkündür — en iyisi bizi doğrudan aramanız. Online rezervasyonlar planlı yolculuklar için idealdir.' },
          { q: 'Salzburg Havalimanı\'nda buluşma noktası neresi?', a: 'Şoförlerimiz sizi istediğiniz noktadan alır — varış salonu, terminal çıkışı veya özel bir konum olsun.' },
          { q: 'Alış saatimi değiştirebilir miyim?', a: 'Çoğu durumda alış saatleri esnek şekilde ayarlanabilir. Lütfen değişiklikleri sorunsuz karşılayabilmemiz için zamanında bizimle iletişime geçin.' },
          { q: 'Yolculuğu iptal edebilir miyim?', a: 'Evet, kalkıştan 24 saat öncesine kadar ücretsiz iptal mümkündür. Daha kısa süreli iptaller için lütfen doğrudan bizimle iletişime geçin.' },
        ],
      },
      {
        name: 'Fiyatlar & Hizmet',
        items: [
          { q: 'Yolculuklar için sabit fiyatlar var mı?', a: 'Evet, çoğu transfer için şeffaf sabit fiyatlar sunuyoruz. Güzergah önceden belirlenemiyorsa, kilometre veya zamana göre ücretlendirme yapılabilir.' },
          { q: 'Takside çocuk koltuğu mevcut mu?', a: 'Avusturya\'da taksilerde çocuk koltuğu zorunluluğu yoktur. Yine de isterseniz ücretsiz olarak sağlıyoruz. Lütfen rezervasyon sırasında belirtin.' },
          { q: 'Geç kalırsam ne olur?', a: '10 dakikaya kadar gecikme ücrete dahildir. Sonrasında ek bekleme ücreti uygulanabilir.' },
          { q: 'Hangi ödeme yöntemleri mevcut?', a: 'Nakit veya kartla doğrudan şoföre ödeme yapabilirsiniz.' },
          { q: 'Gece veya tatil günleri için ek ücret var mı?', a: 'Hayır, gece veya tatil günleri için ek ücret uygulamıyoruz. Her zaman anlaşılan fiyatı ödersiniz — gizli maliyet yok.' },
        ],
      },
      {
        name: 'Araçlar & Donanım',
        items: [
          { q: 'Kaç yolcu binebilir?', a: 'En büyük araçlarımız 7 yolcuya kadar yer sunar. Daha büyük gruplar için birden fazla araç aynı anda reserve edilebilir.' },
          { q: 'Grup veya fazla bagaj için araç var mı?', a: 'Evet, aileler, kayak ekipmanı veya ekstra bagaj için ideal geniş vanlarımız ve bol depolama alanlı araçlarımız var.' },
          { q: 'Tüm araçlarda klima var mı?', a: 'Evet, tüm araçlarımızda modern klima sistemi bulunmaktadır.' },
          { q: 'Belirli bir araç seçebilir miyim?', a: 'Evet, istek üzerine sedan veya van gibi farklı araç tiplerinden seçim yapabilirsiniz.' },
          { q: 'Elektrikli araç sunuyor musunuz?', a: 'Evet, yolculuğunuzu daha sürdürülebilir kılmak için giderek daha fazla elektrikli araç kullanıyoruz.' },
        ],
      },
    ],
  },
  fr: {
    title: 'Questions fréquentes',
    subtitle: 'Tout ce que vous devez savoir sur nos services de taxi et transfert',
    categories: [
      {
        name: 'Réservation & Déroulement',
        items: [
          { q: 'Comment puis-je réserver un taxi ou une excursion ?', a: 'Vous pouvez réserver votre course par téléphone, e-mail, WhatsApp, formulaire de réservation ou directement en ligne. Pour les courses de dernière minute, nous recommandons un appel.' },
          { q: 'Avec quel délai puis-je réserver ?', a: 'Les transferts spontanés sont possibles — appelez-nous directement. Les réservations en ligne sont idéales pour les trajets planifiés.' },
          { q: 'Où est le point de rencontre à l\'aéroport de Salzbourg ?', a: 'Nos chauffeurs vous récupèrent au point de rencontre souhaité — hall d\'arrivée, sortie du terminal ou emplacement personnalisé.' },
          { q: 'Puis-je modifier mon heure de prise en charge ?', a: 'Dans la plupart des cas, les horaires peuvent être ajustés. Veuillez nous contacter à temps pour que nous puissions tenir compte des changements.' },
          { q: 'Puis-je annuler une course ?', a: 'Oui, l\'annulation gratuite est possible jusqu\'à 24 heures avant le départ. Pour les annulations plus tardives, contactez-nous directement.' },
        ],
      },
      {
        name: 'Prix & Service',
        items: [
          { q: 'Y a-t-il des prix fixes ?', a: 'Oui, nous proposons des prix fixes transparents pour la plupart des transferts. Si l\'itinéraire ne peut être déterminé à l\'avance, la facturation peut se faire au kilomètre ou au temps.' },
          { q: 'Des sièges enfant sont-ils disponibles ?', a: 'En Autriche, les sièges enfant ne sont pas obligatoires dans les taxis. Si vous en souhaitez un, nous le fournissons gratuitement. Merci de le préciser lors de la réservation.' },
          { q: 'Que se passe-t-il si je suis en retard ?', a: 'Un retard de 10 minutes est inclus dans le tarif. Au-delà, des frais d\'attente supplémentaires peuvent s\'appliquer.' },
          { q: 'Quels modes de paiement sont acceptés ?', a: 'Vous pouvez payer en espèces ou par carte directement auprès du chauffeur.' },
          { q: 'Y a-t-il des suppléments pour les courses de nuit ou les jours fériés ?', a: 'Non, aucun supplément pour les courses de nuit ou les jours fériés. Vous payez toujours le prix convenu — sans frais cachés.' },
        ],
      },
      {
        name: 'Véhicules & Équipement',
        items: [
          { q: 'Combien de passagers peuvent voyager ?', a: 'Nos plus grands véhicules accueillent jusqu\'à 7 passagers. Pour les groupes plus importants, plusieurs véhicules peuvent être réservés.' },
          { q: 'Y a-t-il des véhicules pour les groupes ou beaucoup de bagages ?', a: 'Oui, nous disposons de vans spacieux avec beaucoup d\'espace de rangement — idéal pour les familles, l\'équipement de ski ou les bagages supplémentaires.' },
          { q: 'Tous les véhicules sont-ils climatisés ?', a: 'Oui, tous nos véhicules sont équipés d\'une climatisation moderne.' },
          { q: 'Puis-je choisir un véhicule spécifique ?', a: 'Oui, vous pouvez choisir entre différents types de véhicules comme berline ou van.' },
          { q: 'Proposez-vous des véhicules électriques ?', a: 'Oui, nous utilisons de plus en plus de véhicules électriques pour rendre votre trajet plus durable.' },
        ],
      },
    ],
  },
  ru: {
    title: 'Часто задаваемые вопросы',
    subtitle: 'Всё, что нужно знать о наших услугах такси и трансфера',
    categories: [
      {
        name: 'Бронирование и процесс',
        items: [
          { q: 'Как забронировать такси или экскурсию?', a: 'Вы можете забронировать поездку по телефону, электронной почте, WhatsApp, через форму бронирования или онлайн на нашем сайте. Для срочных поездок рекомендуем позвонить.' },
          { q: 'За какое время можно забронировать поездку?', a: 'Спонтанные трансферы возможны — лучше позвонить нам напрямую. Онлайн-бронирование подходит для запланированных поездок.' },
          { q: 'Где место встречи в аэропорту Зальцбурга?', a: 'Наши водители заберут вас в удобном для вас месте — зал прилёта, выход из терминала или индивидуальная точка.' },
          { q: 'Можно ли изменить время подачи?', a: 'В большинстве случаев время можно гибко скорректировать. Пожалуйста, свяжитесь с нами заблаговременно.' },
          { q: 'Можно ли отменить поездку?', a: 'Да, бесплатная отмена возможна за 24 часа до отправления. При более поздней отмене свяжитесь с нами напрямую.' },
        ],
      },
      {
        name: 'Цены и сервис',
        items: [
          { q: 'Есть ли фиксированные цены?', a: 'Да, для большинства трансферов мы предлагаем прозрачные фиксированные цены. Если маршрут невозможно определить заранее, оплата может быть по километрам или времени.' },
          { q: 'Есть ли детские кресла?', a: 'В Австрии детские кресла в такси не обязательны. Если вы хотите, мы предоставим их бесплатно. Укажите это при бронировании.' },
          { q: 'Что будет, если я опоздаю?', a: 'Задержка до 10 минут включена в стоимость. После этого может взиматься дополнительная плата за ожидание.' },
          { q: 'Какие способы оплаты доступны?', a: 'Вы можете оплатить наличными или картой непосредственно водителю.' },
          { q: 'Есть ли доплаты за ночные или праздничные поездки?', a: 'Нет, у нас нет доплат за ночные или праздничные поездки. Вы всегда платите согласованную цену — без скрытых расходов.' },
        ],
      },
      {
        name: 'Автомобили и оснащение',
        items: [
          { q: 'Сколько пассажиров может ехать?', a: 'Наши самые большие автомобили вмещают до 7 пассажиров. Для больших групп можно забронировать несколько автомобилей.' },
          { q: 'Есть ли автомобили для групп или большого багажа?', a: 'Да, у нас есть просторные фургоны с большим багажным пространством — идеально для семей, лыжного снаряжения или дополнительного багажа.' },
          { q: 'Все ли автомобили оснащены кондиционером?', a: 'Да, все наши автомобили оснащены современным кондиционером.' },
          { q: 'Можно ли выбрать конкретный автомобиль?', a: 'Да, по желанию вы можете выбрать между седаном и фургоном.' },
          { q: 'Предлагаете ли вы электромобили?', a: 'Да, мы всё больше используем электромобили для более экологичных поездок.' },
        ],
      },
    ],
  },
  it: {
    title: 'Domande frequenti',
    subtitle: 'Tutto quello che devi sapere sui nostri servizi taxi e transfer',
    categories: [
      {
        name: 'Prenotazione e procedura',
        items: [
          { q: 'Come posso prenotare un taxi o un tour?', a: 'Puoi prenotare comodamente per telefono, e-mail, WhatsApp, modulo di prenotazione o direttamente online. Per corse urgenti, consigliamo di chiamare.' },
          { q: 'Con quanto anticipo posso prenotare?', a: 'I trasferimenti spontanei sono possibili — è meglio chiamarci direttamente. Le prenotazioni online sono ideali per viaggi pianificati.' },
          { q: 'Dov\'è il punto d\'incontro all\'aeroporto di Salisburgo?', a: 'I nostri autisti vi prelevano nel punto desiderato — sala arrivi, uscita terminal o posizione personalizzata.' },
          { q: 'Posso modificare l\'orario di prelievo?', a: 'Nella maggior parte dei casi, gli orari possono essere adattati. Contattaci per tempo per gestire le modifiche senza problemi.' },
          { q: 'Posso cancellare una corsa?', a: 'Sì, la cancellazione gratuita è possibile fino a 24 ore prima della partenza. Per cancellazioni più tardive, contattaci direttamente.' },
        ],
      },
      {
        name: 'Prezzi e servizio',
        items: [
          { q: 'Ci sono prezzi fissi?', a: 'Sì, offriamo prezzi fissi trasparenti per la maggior parte dei trasferimenti. Se il percorso non può essere determinato in anticipo, la fatturazione può essere per chilometri o tempo.' },
          { q: 'Sono disponibili seggiolini per bambini?', a: 'In Austria, i seggiolini non sono obbligatori nei taxi. Se ne desiderate uno, lo forniamo gratuitamente. Indicatelo al momento della prenotazione.' },
          { q: 'Cosa succede se sono in ritardo?', a: 'Un ritardo fino a 10 minuti è incluso nella tariffa. Dopo possono essere applicati costi di attesa aggiuntivi.' },
          { q: 'Quali metodi di pagamento sono accettati?', a: 'Potete pagare comodamente in contanti o con carta direttamente all\'autista.' },
          { q: 'Ci sono supplementi per corse notturne o festive?', a: 'No, non applichiamo supplementi per corse notturne o festive. Pagate sempre il prezzo concordato — nessun costo nascosto.' },
        ],
      },
      {
        name: 'Veicoli e dotazione',
        items: [
          { q: 'Quanti passeggeri possono viaggiare?', a: 'I nostri veicoli più grandi ospitano fino a 7 passeggeri. Per gruppi più numerosi, è possibile prenotare più veicoli.' },
          { q: 'Ci sono veicoli per gruppi o molto bagaglio?', a: 'Sì, disponiamo di van spaziosi con ampio spazio di stoccaggio — ideali per famiglie, attrezzatura da sci o bagagli extra.' },
          { q: 'Tutti i veicoli hanno l\'aria condizionata?', a: 'Sì, tutti i nostri veicoli sono dotati di climatizzazione moderna.' },
          { q: 'Posso scegliere un veicolo specifico?', a: 'Sì, su richiesta potete scegliere tra berlina o van.' },
          { q: 'Offrite veicoli elettrici?', a: 'Sì, utilizziamo sempre più veicoli elettrici per rendere il vostro viaggio più sostenibile.' },
        ],
      },
    ],
  },
  es: {
    title: 'Preguntas frecuentes',
    subtitle: 'Todo lo que necesitas saber sobre nuestros servicios de taxi y transfer',
    categories: [
      {
        name: 'Reserva y proceso',
        items: [
          { q: '¿Cómo puedo reservar un taxi o tour?', a: 'Puede reservar cómodamente por teléfono, correo electrónico, WhatsApp, formulario de reserva o directamente en línea. Para viajes urgentes, recomendamos llamar.' },
          { q: '¿Con cuánta antelación puedo reservar?', a: 'Los traslados espontáneos son posibles — lo mejor es llamarnos directamente. Las reservas en línea son ideales para viajes planificados.' },
          { q: '¿Dónde es el punto de encuentro en el aeropuerto de Salzburgo?', a: 'Nuestros conductores le recogen en el punto deseado — sala de llegadas, salida del terminal o ubicación personalizada.' },
          { q: '¿Puedo cambiar mi hora de recogida?', a: 'En la mayoría de los casos, los horarios se pueden ajustar. Contáctenos con tiempo para gestionar los cambios sin problemas.' },
          { q: '¿Puedo cancelar un viaje?', a: 'Sí, la cancelación gratuita es posible hasta 24 horas antes de la salida. Para cancelaciones más tardías, contáctenos directamente.' },
        ],
      },
      {
        name: 'Precios y servicio',
        items: [
          { q: '¿Hay precios fijos?', a: 'Sí, ofrecemos precios fijos transparentes para la mayoría de los traslados. Si la ruta no puede determinarse con antelación, la facturación puede ser por kilómetros o tiempo.' },
          { q: '¿Hay sillas para niños disponibles?', a: 'En Austria, las sillas para niños no son obligatorias en taxis. Si desea una, la proporcionamos gratuitamente. Indíquelo al reservar.' },
          { q: '¿Qué pasa si llego tarde?', a: 'Un retraso de hasta 10 minutos está incluido en la tarifa. Después pueden aplicarse cargos de espera adicionales.' },
          { q: '¿Qué métodos de pago están disponibles?', a: 'Puede pagar cómodamente en efectivo o con tarjeta directamente al conductor.' },
          { q: '¿Hay recargos por viajes nocturnos o festivos?', a: 'No, no tenemos recargos por viajes nocturnos o festivos. Siempre paga el precio acordado — sin costos ocultos.' },
        ],
      },
      {
        name: 'Vehículos y equipamiento',
        items: [
          { q: '¿Cuántos pasajeros pueden viajar?', a: 'Nuestros vehículos más grandes acomodan hasta 7 pasajeros. Para grupos más grandes, se pueden reservar varios vehículos.' },
          { q: '¿Hay vehículos para grupos o mucho equipaje?', a: 'Sí, tenemos furgonetas espaciosas con amplio espacio de almacenamiento — ideal para familias, equipo de esquí o equipaje extra.' },
          { q: '¿Todos los vehículos tienen aire acondicionado?', a: 'Sí, todos nuestros vehículos cuentan con climatización moderna.' },
          { q: '¿Puedo elegir un vehículo específico?', a: 'Sí, puede elegir entre diferentes tipos de vehículos como sedán o furgoneta.' },
          { q: '¿Ofrecen vehículos eléctricos?', a: 'Sí, utilizamos cada vez más vehículos eléctricos para hacer su viaje más sostenible.' },
        ],
      },
    ],
  },
  ar: {
    title: 'الأسئلة الشائعة',
    subtitle: 'كل ما تحتاج معرفته عن خدمات التاكسي والنقل لدينا',
    categories: [
      {
        name: 'الحجز والإجراءات',
        items: [
          { q: 'كيف يمكنني حجز تاكسي أو جولة؟', a: 'يمكنك الحجز بسهولة عبر الهاتف أو البريد الإلكتروني أو واتساب أو نموذج الحجز أو مباشرة عبر موقعنا. للرحلات العاجلة، ننصح بالاتصال.' },
          { q: 'ما أقرب وقت يمكنني الحجز فيه؟', a: 'النقل الفوري ممكن — الأفضل الاتصال بنا مباشرة. الحجز عبر الإنترنت مثالي للرحلات المخطط لها.' },
          { q: 'أين نقطة الالتقاء في مطار سالزبورغ؟', a: 'يستقبلك سائقونا في النقطة التي تختارها — صالة الوصول أو مخرج المحطة أو موقع مخصص.' },
          { q: 'هل يمكنني تغيير وقت الاستلام؟', a: 'في معظم الحالات، يمكن تعديل المواعيد بمرونة. يرجى التواصل معنا في الوقت المناسب.' },
          { q: 'هل يمكنني إلغاء الرحلة؟', a: 'نعم، الإلغاء المجاني ممكن حتى 24 ساعة قبل الانطلاق. للإلغاء في وقت أقرب، تواصل معنا مباشرة.' },
        ],
      },
      {
        name: 'الأسعار والخدمة',
        items: [
          { q: 'هل توجد أسعار ثابتة؟', a: 'نعم، نقدم أسعارًا ثابتة وشفافة لمعظم عمليات النقل. إذا تعذر تحديد المسار مسبقًا، قد يتم الحساب بالكيلومترات أو الوقت.' },
          { q: 'هل تتوفر مقاعد أطفال؟', a: 'في النمسا، مقاعد الأطفال ليست إلزامية في سيارات الأجرة. إذا رغبت في ذلك، نوفرها مجانًا. يرجى الإشارة عند الحجز.' },
          { q: 'ماذا يحدث إذا تأخرت؟', a: 'التأخير حتى 10 دقائق مشمول في السعر. بعد ذلك قد تُطبق رسوم انتظار إضافية.' },
          { q: 'ما طرق الدفع المتاحة؟', a: 'يمكنك الدفع نقدًا أو بالبطاقة مباشرة للسائق.' },
          { q: 'هل هناك رسوم إضافية للرحلات الليلية أو في العطلات؟', a: 'لا، لا نفرض أي رسوم إضافية للرحلات الليلية أو في العطلات. تدفع دائمًا السعر المتفق عليه — بدون تكاليف خفية.' },
        ],
      },
      {
        name: 'المركبات والتجهيزات',
        items: [
          { q: 'كم عدد الركاب الذين يمكنهم السفر؟', a: 'أكبر مركباتنا تتسع لـ 7 ركاب. للمجموعات الأكبر، يمكن حجز عدة مركبات.' },
          { q: 'هل توجد مركبات للمجموعات أو الأمتعة الكثيرة؟', a: 'نعم، لدينا حافلات صغيرة واسعة بمساحة تخزين كبيرة — مثالية للعائلات ومعدات التزلج والأمتعة الإضافية.' },
          { q: 'هل جميع المركبات مكيفة؟', a: 'نعم، جميع مركباتنا مجهزة بتكييف حديث.' },
          { q: 'هل يمكنني اختيار مركبة محددة؟', a: 'نعم، يمكنك الاختيار بين أنواع مختلفة مثل سيدان أو فان.' },
          { q: 'هل تقدمون مركبات كهربائية؟', a: 'نعم، نستخدم بشكل متزايد المركبات الكهربائية لجعل رحلتك أكثر استدامة.' },
        ],
      },
    ],
  },
};

const FAQSection = () => {
  const { lang } = useLanguage();
  const data = faqData[lang as keyof typeof faqData] || faqData.en;

  // JSON-LD FAQ schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.categories.flatMap((cat) =>
      cat.items.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      }))
    ),
  };

  return (
    <section className="py-20 md:py-28 bg-muted/30" id="faq">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <div className="container max-w-4xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            {data.title}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{data.subtitle}</p>
          <div className="w-16 h-1 bg-gradient-gold mx-auto mt-6 rounded-full" />
        </motion.div>

        <div className="space-y-8">
          {data.categories.map((cat, ci) => (
            <motion.div
              key={ci}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: ci * 0.1 }}
            >
              <h3 className="font-display text-xl font-semibold text-primary mb-4">{cat.name}</h3>
              <Accordion type="single" collapsible className="bg-card rounded-2xl border border-border shadow-card overflow-hidden">
                {cat.items.map((item, i) => (
                  <AccordionItem key={i} value={`${ci}-${i}`} className="border-border px-6">
                    <AccordionTrigger className="text-left text-foreground hover:text-primary hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
